import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FiArrowUpRight, FiCheck } from 'react-icons/fi';

import NewProperty from '../pages/NewProperty';
import NewPropertyPhotos from '../pages/NewPropertyPhotos';
import NewPropertyPro from '../pages/NewPropertyPro';
import NewPropertyPreview from './NewPropertyPreview';
import NewPropertyFinalPreview from './NewPropertyFinalPreview';
import PublicationIntent from './PublicationIntent';

import { createProperty } from '../services/propertiesService';
import { uploadDocument, uploadImage, uploadVideo } from '../services/cloudinary';
import { useAuth } from '../auth/AuthContext';
import styled from 'styled-components';

const STEPS = ['Objetivo', 'Tu propiedad', 'Fotos', 'Características', 'Descripción', 'Revisión'];

function PublishProperty() {

  const [step, setStep] = useState(1);
  const [propertyData, setPropertyData] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [capacity, setCapacity] = useState(null);
  const [limitReachedAtPublish, setLimitReachedAtPublish] = useState(false);

  const navigate = useNavigate();
  const { user } = useAuth();

  useEffect(() => {
    // Cada pantalla del asistente es un paso nuevo: en móvil nunca debe heredar
    // el scroll del paso anterior ni aparecer recortada bajo el encabezado.
    window.requestAnimationFrame(() => window.scrollTo({ top: 0, left: 0, behavior: 'auto' }));
  }, [step]);

  const withProgress = (content) => (
    <WizardPage>
      <Progress aria-label={`Paso ${step} de ${STEPS.length}`}>
        <ProgressIntro><span>PUBLICAR EN NIDO</span><strong>Paso {step} de {STEPS.length}</strong></ProgressIntro>
        <ProgressTrack>{STEPS.map((label, index) => <ProgressStep key={label} $active={index + 1 <= step} $current={index + 1 === step}><i>{index + 1}</i><span>{label}</span></ProgressStep>)}</ProgressTrack>
      </Progress>
      {content}
    </WizardPage>
  );

  const handlePublish = async () => {

    try {

      setLoading(true);
      setError(null);

      if (!user) {
        throw new Error('Tu sesión venció. Volvé a ingresar para publicar la propiedad.');
      }

      const uploadedImages = [];

      for (const [index, photo] of (propertyData.images || []).entries()) {
        try {
          if (typeof photo === 'string') uploadedImages.push(photo);
          else if (photo.url) uploadedImages.push(photo.url);
          else if (photo.file) uploadedImages.push(await uploadImage(photo.file));
        } catch (uploadError) {
          throw new Error(`No pudimos subir la foto ${index + 1}. ${uploadError?.message || 'Intentá nuevamente.'}`);
        }

      }

      if (uploadedImages.length < 4) {
        throw new Error('La publicación necesita al menos 4 fotos cargadas correctamente.');
      }

      const finalPropertyData = {
        ...propertyData,
        images: uploadedImages,
        video: propertyData.video?.file ? await uploadVideo(propertyData.video.file) : propertyData.video || undefined,
        floorPlans: await Promise.all((propertyData.floorPlans || []).map(async (plan) => ({
          type: plan.type,
          title: plan.title,
          url: plan.file ? (plan.type === 'pdf' ? await uploadDocument(plan.file) : await uploadImage(plan.file)) : plan.url,
        }))),
      };

      const createdProperty = await createProperty(finalPropertyData);

      if (!createdProperty?._id) {
        throw new Error('El servidor no confirmó la publicación. Intentá nuevamente.');
      }

      navigate(`/properties/${createdProperty._id}`, {
  replace: true,
  state: { from: "/mis-publicaciones" }
});

    } catch (err) {

      console.error(err);
      setError(err.message || 'Error al publicar la propiedad');
      if (err.code === 'PLAN_LISTING_LIMIT_REACHED') {
        setCapacity(err.details);
        setLimitReachedAtPublish(true);
      }
      throw err;

    } finally {

      setLoading(false);

    }

  };

  if (limitReachedAtPublish && capacity && !capacity.canPublish) {
    return <CapacityPage>
      <LimitCard role="alert">
        <LimitVisual><span>{capacity.activeListings}</span><small>de {capacity.listingLimit}</small></LimitVisual>
        <div>
          <LimitEyebrow>{capacity.requiresPlan ? 'ELEGÍ TU PLAN' : `CUOTA COMPLETA · ${capacity.plan}`}</LimitEyebrow>
          <h1>{capacity.requiresPlan ? 'Tu publicación está lista. Elegí cómo activarla.' : 'Tu publicación está lista; necesitás ampliar el cupo.'}</h1>
          <p>{capacity.requiresPlan ? 'Guardamos el trabajo de esta sesión. Elegí el plan que mejor se adapte a tu perfil para poder confirmar el aviso.' : 'Ya utilizaste todas las publicaciones activas de tu plan. Podés ampliar el cupo o administrar las publicaciones existentes.'}</p>
          <LimitBenefits><span><FiCheck/> Más publicaciones activas</span><span><FiCheck/> Mayor visibilidad</span><span><FiCheck/> Gestión desde tu panel</span></LimitBenefits>
          <LimitActions><Link to={capacity.upgradePath || '/client/planes'}>Ver opciones para publicar <FiArrowUpRight/></Link><button className="quiet" type="button" onClick={()=>setLimitReachedAtPublish(false)}>Volver a revisar el aviso</button><Link className="quiet" to="/mis-publicaciones">Administrar mis publicaciones</Link></LimitActions>
          <small>También podés pausar o finalizar una publicación activa para liberar un lugar.</small>
        </div>
      </LimitCard>
    </CapacityPage>;
  }

  switch (step) {

    case 1:
      return withProgress(<PublicationIntent value={propertyData.operation} onSubmit={(data) => {
        setPropertyData(prev => {
          if (!prev.operation || prev.operation === data.operation) return { ...prev, ...data };
          const next = { ...prev, ...data };
          ['vacation','rental','isAptoCredito','age','orientation','condition','expenses','price'].forEach(key => delete next[key]);
          return next;
        });
        setStep(2);
        window.requestAnimationFrame(() => window.scrollTo({ top: 0, behavior: 'auto' }));
      }} />);

    case 2:

      return withProgress(

        <NewProperty
          initialData={propertyData}
          onBack={() => setStep(1)}
          onSubmit={(data) => {

            setPropertyData(prev => ({
              ...prev,
              ...data
            }));

            setStep(3);

          }}
        />

      );

    case 3:

      return withProgress(

        <NewPropertyPhotos
          onBack={() => {
            setStep(2);
            window.requestAnimationFrame(() => window.scrollTo({ top: 0, left: 0, behavior: 'auto' }));
          }}
          initialPhotos={propertyData.images || []}
          initialVideo={propertyData.video || null}
          initialFloorPlans={propertyData.floorPlans || []}
          initialVirtualTourUrl={propertyData.virtualTourUrl || ''}

            onSubmit={(media) => {

              setPropertyData(prev => ({
                ...prev,
                images: media.images,
                video: media.video,
                floorPlans: media.floorPlans,
                virtualTourUrl: media.virtualTourUrl
              }));

            setStep(4);

          }}
        />

      );

    case 4:

      return withProgress(

        <NewPropertyPro
          onBack={() => setStep(3)}
          initialData={propertyData}

          onSubmit={(data) => {

            setPropertyData(prev => ({
              ...prev,
              ...data
            }));

            setStep(5);

          }}
        />

      );

    case 5:

      return withProgress(

        <NewPropertyPreview
          photos={propertyData.images || []}
          features={propertyData.features || {}}

          onBack={() => setStep(4)}

          onNext={(previewData) => {

            setPropertyData(prev => ({
              ...prev,
              description: previewData.description
            }));

            setStep(6);

          }}
        />

      );

    case 6:

      return withProgress(

        <NewPropertyFinalPreview
          propertyData={propertyData}
          onBack={() => setStep(5)}
          onUpdate={(data) => setPropertyData((prev) => ({ ...prev, ...data }))}
          onPublish={handlePublish}
          loading={loading}
          error={error}
        />

      );

    default:
      return null;

  }

}

export default PublishProperty;

const WizardPage = styled.main`min-width:0;min-height:100vh;overflow-x:clip;background:#f7f5ef;color:#29474f;`;
const Progress = styled.header`
  position:relative;
  z-index:10;
  max-width:1150px;
  margin:0 auto -18px;
  padding:34px 30px 0;

  @media(max-width:700px){
    margin-bottom:0;
    padding:22px 16px 0;
  }
`;
const ProgressIntro = styled.div`display:flex;align-items:center;justify-content:space-between;gap:12px;margin-bottom:17px;span{color:#a95336;font-size:14px;font-weight:760;letter-spacing:.13em;}strong{color:#536568;font-size:14px;font-weight:650;white-space:nowrap;}@media(max-width:380px){span{font-size:12px;}strong{font-size:12px;}}`;
const ProgressTrack = styled.div`display:grid;grid-template-columns:repeat(6,1fr);gap:4px;`;
const ProgressStep = styled.div`min-width:0;position:relative;padding-top:11px;border-top:2px solid ${({$active})=>$active?'#b45f40':'#d7ddda'};color:${({$current})=>$current?'#29474f':'#647477'};i{display:none;}span{font-size:14px;font-weight:${({$current})=>$current?'720':'580'};line-height:1.4;}@media(max-width:900px){span{display:${({$current})=>$current?'block':'none'};font-size:14px;white-space:nowrap;}}`;
const CapacityPage=styled.main`min-height:calc(100vh - 110px);display:grid;place-items:center;padding:64px 24px;overflow-x:hidden;background:radial-gradient(circle at 82% 20%,#f8e8df 0,transparent 34%),#f7f5ef;color:#29474f;@media(max-width:560px){padding:32px 14px;}`;
const LimitCard=styled.section`width:min(1040px,100%);min-width:0;display:grid;grid-template-columns:260px minmax(0,1fr);gap:64px;align-items:center;padding:64px;border:1px solid #dce1de;border-radius:30px;background:rgba(255,255,255,.92);box-shadow:0 28px 80px rgba(41,71,79,.12);h1{max-width:680px;margin:8px 0 18px;font-size:clamp(38px,5vw,68px);line-height:.98;letter-spacing:-.045em;overflow-wrap:anywhere;}p{max-width:680px;font-size:18px;line-height:1.6;color:#647477;}small{display:block;margin-top:22px;font-size:14px;color:#718083;}@media(max-width:760px){grid-template-columns:1fr;gap:28px;padding:30px 20px;border-radius:22px;h1{font-size:clamp(31px,9vw,39px);}}`;
const LimitVisual=styled.div`width:min(220px,70vw);aspect-ratio:1;margin:auto;border-radius:50%;display:grid;place-content:center;text-align:center;background:#294f57;color:#fff;box-shadow:0 0 0 12px #f3e4dd;span{font-size:76px;font-weight:760;line-height:.9;}small{margin:10px 0 0;color:#dfe9e6;font-size:16px;}`;
const LimitEyebrow=styled.span`font-size:14px;font-weight:800;letter-spacing:.14em;color:#b45f40;`;
const LimitBenefits=styled.div`display:flex;flex-wrap:wrap;gap:10px;margin:26px 0;span{display:flex;align-items:center;gap:7px;padding:10px 13px;border-radius:999px;background:#eef2ef;font-size:14px;font-weight:680;}svg{color:#b45f40;}`;
const LimitActions=styled.div`display:flex;align-items:center;gap:14px;flex-wrap:wrap;a,button{display:inline-flex;align-items:center;justify-content:center;gap:9px;min-height:52px;padding:0 22px;border:0;border-radius:999px;background:#c86d4b;color:white;text-decoration:none;font:inherit;font-weight:760;cursor:pointer;}.quiet{background:transparent;color:#29474f;border:1px solid #cfd7d4;}@media(max-width:560px){a,button{width:100%;}}`;
